import React, { useState, useCallback, useRef, useEffect } from 'react';
import { observer } from 'mobx-react';
import { TextField, Select, Pagination, Checkbox, Icon, Button } from '@ann/web-components';
import { useNotesTabPage } from './hooks';
import { NoteCard } from './components';
import { CATEGORY_OPTIONS } from '../../constants';
import NotesSlideOut from '../../../Activity/components/PreviewSlideOut/NotesSlideOut';
import {
  TabContainer,
  HeaderContainer,
  TitleSection,
  Title,
  ItemCount,
  ActionsContainer,
  FiltersContainer,
  SearchContainer,
  FilterDropdownWrapper,
  ListContainer,
  EmptyState,
  EmptyIcon,
  EmptyTitle,
  EmptyDescription,
  PaginationWrapper,
  PaginationInfo,
  PaginationText,
  LoadingContainer,
  SkeletonCard,
  SelectionActionBar,
  SelectionInfo,
  SelectedCount,
  SelectAllButton,
  SelectionActions,
  ActionButton,
  CloseSelectionButton,
} from './NotesTab.styled';

const NotesTab = observer(() => {
  const {
    notes,
    meta,
    loading,
    error,
    filters,
    selectedIds,
    selectAll,
    handleSearchChange,
    handleCategoryChange,
    toggleSelect,
    toggleSelectAll,
    handlePageChange,
    handleItemsPerPageChange,
    handleRefresh,
    createNote,
    getNoteDetail,
    updateNote,
    pinNote,
  } = useNotesTabPage();

  // Local search state for debouncing
  const [localSearchValue, setLocalSearchValue] = useState(filters.search || '');
  const searchTimeoutRef = useRef(null);

  // SlideOut state
  const [showCreateSlideOut, setShowCreateSlideOut] = useState(false);
  const [isLoadingUpdateSlideOut, setIsLoadingUpdateSlideOut] = useState(false);
  const [showUpdateSlideOut, setShowUpdateSlideOut] = useState(false);
  const [selectedNoteData, setSelectedNoteData] = useState(null);

  // Debounced search handler
  const handleLocalSearchChange = useCallback(
    (value) => {
      setLocalSearchValue(value);
      
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
      
      searchTimeoutRef.current = setTimeout(() => {
        handleSearchChange(value);
      }, 500);
    },
    [handleSearchChange]
  );

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, []);

  // Sync local search with filter state
  useEffect(() => {
    if (filters.search !== localSearchValue && !searchTimeoutRef.current) {
      setLocalSearchValue(filters.search);
    }
  }, [filters.search, localSearchValue]);

  // Handle Add Note
  const handleAddNote = useCallback(() => {
    setShowCreateSlideOut(true);
  }, []);

  // Handle Edit Note (view/update slideout)
  // Open immediately with list data so panel appears without waiting for getNoteDetail.
  // Then fetch detail in background and update when ready (fixes first-time delay).
  const handleEditNote = useCallback(
    async (note) => {
      setIsLoadingUpdateSlideOut(true);
      const listData = {
        ...note,
        note_title: note.title,
        note_content: note.description,
        ownerFullName: note.owner?.name,
        lastUpdate: note.lastUpdated,
      };
      setSelectedNoteData(listData);
      setShowUpdateSlideOut(true);
      try {
        const noteDetail = await getNoteDetail(note.noteUuid || note.id);
        const detailData = noteDetail?.body || noteDetail;
        
        // Merge: keep owner/date info from list, add detail content
        // The list API has owner/dates, detail API has full content
        const mergedData = {
          ...note,        // Original note from list (has owner, createdAt, lastUpdated)
          ...detailData,  // Detail response (has full content)
          // Ensure owner info from list is preserved
          ownerFullName: note.owner?.name || detailData.ownerFullName,
          createdAt: note.createdAt || detailData.createdAt,
          lastUpdate: note.lastUpdated || detailData.lastUpdate,
          category: note.category || detailData.category || 'GENERAL',
          raw: detailData.raw,
        };
        setSelectedNoteData(mergedData);
      } catch (err) {
        console.error('Error getting note detail:', err);
      } finally {
        setIsLoadingUpdateSlideOut(false);
      }
    },
    [getNoteDetail]
  );

  // Handle Pin Note - uses pinNote from context
  const handlePinNote = useCallback((noteId, pinned) => {
    pinNote(noteId, pinned);
  }, [pinNote]);

  // Handle SlideOut close
  const handleCreateSlideOutClose = useCallback(() => {
    setShowCreateSlideOut(false);
  }, []);

  const handleUpdateSlideOutClose = useCallback(() => {
    setShowUpdateSlideOut(false);
    setSelectedNoteData(null);
  }, []);

  // Handle Note Created
  const handleNoteCreated = useCallback(() => {
    setShowCreateSlideOut(false);
  }, []);

  // Handle Note Updated
  const handleNoteUpdated = useCallback(() => {
    setShowUpdateSlideOut(false);
    setSelectedNoteData(null);
  }, []);

  // Handle Archive
  const handleArchive = useCallback(() => {
    console.log('Archive notes:', Array.from(selectedIds));
    // TODO: Implement archive functionality
  }, [selectedIds]);

  // Handle Delete
  const handleDelete = useCallback(() => {
    console.log('Delete notes:', Array.from(selectedIds));
    // TODO: Implement delete functionality
  }, [selectedIds]);

  // Clear selection
  const clearSelection = useCallback(() => {
    if (selectAll) {
      toggleSelectAll();
    } else {
      // Clear each selected item
      selectedIds.forEach((id) => toggleSelect(id));
    }
  }, [selectedIds, selectAll, toggleSelectAll, toggleSelect]);

  const hasSelection = selectedIds.size > 0;

  // Render loading skeleton
  if (loading && notes.length === 0) {
    return (
      <TabContainer>
        <HeaderContainer>
          <TitleSection>
            <Title>Notes</Title>
          </TitleSection>
          <ActionsContainer>
            <Button
              label="Add note"
              type="primary"
              size="medium"
              iconLeft="plus"
              onClick={handleAddNote}
              disabled
            />
          </ActionsContainer>
        </HeaderContainer>
        <LoadingContainer>
          {[1, 2, 3, 4, 5].map((i) => (
            <SkeletonCard key={i} />
          ))}
        </LoadingContainer>
      </TabContainer>
    );
  }

  // Render error state
  if (error && notes.length === 0) {
    return (
      <TabContainer>
        <HeaderContainer>
          <TitleSection>
            <Title>Notes</Title>
          </TitleSection>
          <ActionsContainer>
            <Button
              label="Add note"
              type="primary"
              size="medium"
              iconLeft="plus"
              onClick={handleAddNote}
            />
          </ActionsContainer>
        </HeaderContainer>
        <EmptyState>
          <EmptyIcon>
            <Icon name="error" width="48px" height="48px" />
          </EmptyIcon>
          <EmptyTitle>Failed to load notes</EmptyTitle>
          <EmptyDescription>
            {error.message || 'An error occurred. Please try again.'}
          </EmptyDescription>
          <Button label="Retry" type="secondary" onClick={handleRefresh} />
        </EmptyState>
      </TabContainer>
    );
  }

  return (
    <TabContainer>
      {/* Header */}
      <HeaderContainer>
        <TitleSection>
          <Title>Notes</Title>
          <ItemCount>{meta.total_items} notes</ItemCount>
        </TitleSection>
        <ActionsContainer>
          <Button
            label="Add note"
            type="primary"
            size="medium"
            iconLeft="plus"
            onClick={handleAddNote}
            disabled={loading}
          />
        </ActionsContainer>
      </HeaderContainer>

      {/* Filters */}
      <FiltersContainer>
        <SearchContainer>
          <TextField
            iconLeft="search"
            placeholder="Search notes..."
            value={localSearchValue}
            onChange={handleLocalSearchChange}
            size="small"
            disabled={loading}
          />
        </SearchContainer>
        <FilterDropdownWrapper>
          <Select
            placeholder="All Categories"
            value={filters.category}
            onChange={(selected) => handleCategoryChange(selected?.value || selected)}
            size="small"
            width="auto"
            minWidth="140px"
            disabled={loading}
          >
            <Select.Menu>
              {CATEGORY_OPTIONS.map((option) => (
                <Select.Item key={option.value} value={option.value} label={option.label}>
                  {option.label}
                </Select.Item>
              ))}
            </Select.Menu>
          </Select>
        </FilterDropdownWrapper>
      </FiltersContainer>

      {/* Selection Action Bar */}
      {hasSelection && (
        <SelectionActionBar>
          <SelectionInfo>
            <SelectedCount>{selectedIds.size} selected</SelectedCount>
            <SelectAllButton onClick={toggleSelectAll}>
              {selectAll ? 'Deselect All' : 'Select All'}
            </SelectAllButton>
          </SelectionInfo>
          <SelectionActions>
            <ActionButton onClick={handleArchive}>
              Archive
            </ActionButton>
            <ActionButton onClick={handleDelete}>
              Delete
            </ActionButton>
            <CloseSelectionButton onClick={clearSelection} title="Clear selection">
              <Icon name="close" width="18px" height="18px" />
            </CloseSelectionButton>
          </SelectionActions>
        </SelectionActionBar>
      )}

      {/* List */}
      {notes.length === 0 ? (
        <EmptyState>
          <EmptyIcon>
            <Icon name="notes" width="48px" height="48px" />
          </EmptyIcon>
          <EmptyTitle>No notes yet</EmptyTitle>
          <EmptyDescription>
            Add your first note to keep track of important information.
          </EmptyDescription>
          <Button
            label="Add note"
            type="primary"
            iconLeft="plus"
            onClick={handleAddNote}
          />
        </EmptyState>
      ) : (
        <>
          {/* Note Cards */}
          <ListContainer>
            {notes.map((note) => (
              <NoteCard
                key={note.id}
                note={note}
                isSelected={selectedIds.has(note.id)}
                onSelect={toggleSelect}
                onEdit={handleEditNote}
                onPin={handlePinNote}
              />
            ))}
          </ListContainer>

          {/* Pagination */}
          <PaginationWrapper>
            <PaginationInfo>
              <PaginationText>
                Showing {(meta.current_page - 1) * meta.per_page + 1} to{' '}
                {Math.min(meta.current_page * meta.per_page, meta.total_items)} of{' '}
                {meta.total_items} notes
              </PaginationText>
              <Pagination
                currentPage={meta.current_page}
                itemsPerPage={meta.per_page}
                onItemsPerPageChange={handleItemsPerPageChange}
                onPageChange={handlePageChange}
                totalItems={meta.total_items}
                totalPages={meta.total_pages}
                disabled={loading}
              />
            </PaginationInfo>
          </PaginationWrapper>
        </>
      )}


      {/* Create Note SlideOut */}
      <NotesSlideOut
        open={showCreateSlideOut}
        onClose={handleCreateSlideOutClose}
        mode="create"
        onCreateNote={async (payload) => {
          await createNote(payload);
          handleNoteCreated();
        }}
      />

      {/* Update Note SlideOut */}
      <NotesSlideOut
        open={showUpdateSlideOut}
        onClose={handleUpdateSlideOutClose}
        mode="update"
        noteData={selectedNoteData}
        isLoading={isLoadingUpdateSlideOut}
        onUpdateNote={async (payload) => {
          await updateNote(payload);
          handleNoteUpdated();
        }}
      />
    </TabContainer>
  );
});

export default NotesTab;
